import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Save, LogOut, ArrowLeft, Plus, Trash2, Upload, Check, AlertCircle,
  Globe, Search, Bot, Shield, TrendingUp, Palette, ShoppingCart,
  Rocket, Calendar, Users, Handshake, FolderCheck, Clock, Headphones,
  Monitor, Building2, Truck, Wheat, Factory, Store, Heart, GraduationCap,
  Home, ShoppingBag, DollarSign, UtensilsCrossed, Star,
  Database, Target, Car, KeyRound, HardHat,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_OPTIONS = [
  'Globe','Search','Bot','Shield','TrendingUp','Palette','ShoppingCart','Rocket',
  'Calendar','Users','Handshake','FolderCheck','Clock','Headphones','Monitor',
  'Building2','Truck','Wheat','Factory','Store','Heart','GraduationCap',
  'Home','ShoppingBag','DollarSign','UtensilsCrossed','Star',
  'Database','Target','Car','KeyRound','HardHat',
];

const ICON_MAP = {
  Globe, Search, Bot, Shield, TrendingUp, Palette, ShoppingCart, Rocket,
  Calendar, Users, Handshake, FolderCheck, Clock, Headphones, Monitor,
  Building2, Truck, Wheat, Factory, Store, Heart, GraduationCap,
  Home, ShoppingBag, DollarSign, UtensilsCrossed, Star,
  Database, Target, Car, KeyRound, HardHat,
};

const SECTIONS = [
  { key: 'company_info', label: 'Company Info' },
  { key: 'hero', label: 'Hero Section' },
  { key: 'services', label: 'Services' },
  { key: 'industries', label: 'Industries' },
  { key: 'success_stories', label: 'Success Stories' },
  { key: 'blogs', label: 'Blog Posts' },
  { key: 'why_choose_us', label: 'Why Choose Us' },
  { key: 'stats', label: 'Stats' },
  { key: 'testimonials', label: 'Testimonials' },
];

const FIELD_CONFIGS = {
  services: [
    { key: 'icon', label: 'Icon', type: 'icon' },
    { key: 'number', label: 'Number', placeholder: '01' },
    { key: 'title', label: 'Title' },
    { key: 'tags', label: 'Tags', type: 'tags' },
    { key: 'description', label: 'Description', type: 'textarea' },
  ],
  stats: [
    { key: 'icon', label: 'Icon', type: 'icon' },
    { key: 'label', label: 'Label' },
    { key: 'number', label: 'Number', type: 'number' },
    { key: 'suffix', label: 'Suffix', placeholder: '+ or k or /7' },
  ],
  why_choose_us: [
    { key: 'number', label: 'Number', placeholder: '01' },
    { key: 'title', label: 'Title' },
    { key: 'description', label: 'Description', type: 'textarea' },
  ],
  industries: [
    { key: 'icon', label: 'Icon', type: 'icon' },
    { key: 'name', label: 'Name' },
    { key: 'blurb', label: 'Description', type: 'textarea' },
    { key: 'image', label: 'Image', type: 'image' },
  ],
  success_stories: [
    { key: 'client', label: 'Client Name' },
    { key: 'industry', label: 'Industry' },
    { key: 'slug', label: 'URL Slug', placeholder: 'cooper-equipment-rentals' },
    { key: 'title', label: 'Story Title' },
    { key: 'summary', label: 'Summary', type: 'textarea' },
    { key: 'image', label: 'Image', type: 'image' },
    { key: 'metric1Value', label: 'Metric 1 Value', placeholder: '+112%' },
    { key: 'metric1Label', label: 'Metric 1 Label', placeholder: 'Qualified Leads' },
    { key: 'metric2Value', label: 'Metric 2 Value' },
    { key: 'metric2Label', label: 'Metric 2 Label' },
    { key: 'metric3Value', label: 'Metric 3 Value' },
    { key: 'metric3Label', label: 'Metric 3 Label' },
    { key: 'services', label: 'Services Provided', type: 'tags' },
  ],
  blogs: [
    { key: 'title', label: 'Title' },
    { key: 'slug', label: 'URL Slug' },
    { key: 'category', label: 'Category' },
    { key: 'date', label: 'Date', placeholder: 'Feb 12, 2026' },
    { key: 'readTime', label: 'Read Time', placeholder: '5 min read' },
    { key: 'excerpt', label: 'Excerpt', type: 'textarea' },
    { key: 'image', label: 'Image', type: 'image' },
  ],
  testimonials: [
    { key: 'company', label: 'Company Name' },
    { key: 'text', label: 'Testimonial', type: 'textarea' },
    { key: 'rating', label: 'Rating (1-5)', type: 'number' },
  ],
};

const apiClient = axios.create({ baseURL: API, withCredentials: true });

// ── Toast ──
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
      type === 'success' ? 'bg-green-600' : 'bg-red-600'
    }`}>
      {type === 'success' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {message}
    </div>
  );
};

// ── Field Components ──
const Field = ({ label, value, onChange, type = 'text', rows, placeholder }) => (
  <div className="space-y-1">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    {type === 'textarea' ? (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={rows || 3}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#FF6B35] focus:outline-none focus:ring-1 focus:ring-[#FF6B35]/20 text-sm resize-none"
      />
    ) : (
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#FF6B35] focus:outline-none focus:ring-1 focus:ring-[#FF6B35]/20 text-sm"
      />
    )}
  </div>
);

const IconPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const IconComp = ICON_MAP[value];
  return (
    <div className="relative">
      <label className="text-sm font-medium text-gray-700 block mb-1">Icon</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:border-gray-300 text-sm w-full"
      >
        {IconComp && <IconComp className="w-4 h-4 text-[#FF6B35]" />}
        <span className="text-gray-700">{value || 'Select icon'}</span>
      </button>
      {open && (
        <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 grid grid-cols-6 gap-1 w-72 max-h-48 overflow-y-auto">
          {ICON_OPTIONS.map((name) => {
            const IC = ICON_MAP[name];
            return (
              <button
                key={name}
                type="button"
                onClick={() => { onChange(name); setOpen(false); }}
                className={`p-2 rounded hover:bg-gray-100 flex items-center justify-center ${value === name ? 'bg-[#FF6B35]/10 ring-1 ring-[#FF6B35]' : ''}`}
                title={name}
              >
                {IC && <IC className="w-4 h-4" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ImageUpload = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await apiClient.post('/cms/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(`${BACKEND_URL}${res.data.url}`);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Image upload failed:', err);
      }
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-gray-700">Image</label>
      <div className="flex items-center gap-3">
        {value && <img src={value} alt="preview" className="w-16 h-16 rounded-lg object-cover border" />}
        <label className="cursor-pointer flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg hover:border-[#FF6B35] text-sm text-gray-500 hover:text-[#FF6B35] transition-colors">
          <Upload className="w-4 h-4" />
          {uploading ? 'Uploading...' : 'Upload'}
          <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        </label>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Or paste image URL"
          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#FF6B35] focus:outline-none"
        />
      </div>
    </div>
  );
};

// ── Section Editors ──
const CompanyInfoEditor = ({ data, setData }) => (
  <div className="grid md:grid-cols-2 gap-4">
    <Field label="Company Name" value={data.name} onChange={(v) => setData({ ...data, name: v })} />
    <Field label="Full Name" value={data.fullName} onChange={(v) => setData({ ...data, fullName: v })} />
    <Field label="Tagline" value={data.tagline} onChange={(v) => setData({ ...data, tagline: v })} />
    <Field label="Subtitle" value={data.subtitle} onChange={(v) => setData({ ...data, subtitle: v })} />
    <div className="md:col-span-2">
      <Field label="Description" value={data.description} onChange={(v) => setData({ ...data, description: v })} type="textarea" />
    </div>
    <Field label="Phone" value={data.phone} onChange={(v) => setData({ ...data, phone: v })} />
    <Field label="Email" value={data.email} onChange={(v) => setData({ ...data, email: v })} />
    <Field label="Address" value={data.address} onChange={(v) => setData({ ...data, address: v })} />
    <Field label="Website" value={data.website} onChange={(v) => setData({ ...data, website: v })} />
  </div>
);

const HeroEditor = ({ data, setData }) => {
  const update = (key) => (v) => setData({ ...data, [key]: v });
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <Field label="Eyebrow" value={data.eyebrow} onChange={update('eyebrow')} placeholder="DIGITAL AGENCY · ESTABLISHED 2026" />
      </div>
      <div className="md:col-span-2">
        <Field label="Headline" value={data.headline} onChange={update('headline')} type="textarea" />
      </div>
      <div className="md:col-span-2">
        <Field label="Subheadline" value={data.subheadline} onChange={update('subheadline')} type="textarea" />
      </div>
      <Field label="Primary CTA Label" value={data.primaryCtaLabel} onChange={update('primaryCtaLabel')} placeholder="Book a Consultation" />
      <Field label="Primary CTA Href" value={data.primaryCtaHref} onChange={update('primaryCtaHref')} placeholder="#contact" />
      <Field label="Secondary CTA Label" value={data.secondaryCtaLabel} onChange={update('secondaryCtaLabel')} placeholder="View Our Work" />
      <Field label="Secondary CTA Href" value={data.secondaryCtaHref} onChange={update('secondaryCtaHref')} placeholder="/success-stories" />
    </div>
  );
};

// ── Render a single field in a list item ──
const ListItemField = ({ field, item, onUpdate }) => {
  if (field.type === 'icon') {
    return <IconPicker value={item[field.key]} onChange={(v) => onUpdate(field.key, v)} />;
  }
  if (field.type === 'image') {
    return (
      <div className="md:col-span-2">
        <ImageUpload value={item[field.key]} onChange={(v) => onUpdate(field.key, v)} />
      </div>
    );
  }
  if (field.type === 'tags') {
    return (
      <div className="md:col-span-2 space-y-1">
        <label className="text-sm font-medium text-gray-700">{field.label}</label>
        <input
          value={(item[field.key] || []).join(', ')}
          onChange={(e) => onUpdate(field.key, e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
          placeholder="Comma separated tags"
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#FF6B35] focus:outline-none"
        />
      </div>
    );
  }
  if (field.type === 'number') {
    return (
      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-700">{field.label}</label>
        <input
          type="number"
          value={item[field.key] || 0}
          onChange={(e) => onUpdate(field.key, parseInt(e.target.value) || 0)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#FF6B35] focus:outline-none"
        />
      </div>
    );
  }
  return (
    <div className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
      <Field
        label={field.label}
        value={item[field.key]}
        onChange={(v) => onUpdate(field.key, v)}
        type={field.type || 'text'}
        placeholder={field.placeholder}
      />
    </div>
  );
};

const ListEditor = ({ data, setData, fields }) => {
  const addItem = () => {
    const newId = Math.max(0, ...data.map((d) => d.id || 0)) + 1;
    const item = { id: newId };
    fields.forEach((f) => {
      if (f.key === 'tags') item[f.key] = [];
      else if (f.key === 'rating') item[f.key] = 5;
      else if (f.key === 'number') item[f.key] = 0;
      else item[f.key] = '';
    });
    setData([...data, item]);
  };

  const updateItem = (index, key, value) => {
    const updated = [...data];
    updated[index] = { ...updated[index], [key]: value };
    setData(updated);
  };

  const removeItem = (index) => setData(data.filter((_, i) => i !== index));

  return (
    <div className="space-y-4">
      {data.map((item, index) => (
        <div key={`item-${item.id || index}`} className="border border-gray-200 rounded-xl p-4 relative group">
          <button
            onClick={() => removeItem(index)}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-red-50 text-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <div className="grid md:grid-cols-2 gap-3">
            {fields.map((f) => (
              <ListItemField
                key={`${item.id}-${f.key}`}
                field={f}
                item={item}
                onUpdate={(key, value) => updateItem(index, key, value)}
              />
            ))}
          </div>
        </div>
      ))}
      <button
        onClick={addItem}
        className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors w-full justify-center text-sm"
      >
        <Plus className="w-4 h-4" /> Add Item
      </button>
    </div>
  );
};

// ── Render active section editor ──
const SectionEditor = ({ activeSection, currentData, updateSectionData }) => {
  if (!currentData) return null;

  if (activeSection === 'company_info') {
    return <CompanyInfoEditor data={currentData} setData={updateSectionData} />;
  }
  if (activeSection === 'hero') {
    return <HeroEditor data={currentData} setData={updateSectionData} />;
  }
  if (FIELD_CONFIGS[activeSection] && Array.isArray(currentData)) {
    return (
      <ListEditor
        data={currentData}
        setData={updateSectionData}
        fields={FIELD_CONFIGS[activeSection]}
      />
    );
  }
  return null;
};

// ── Top Bar ──
const TopBar = ({ saving, onSave, onLogout }) => (
  <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
    <div className="max-w-[1400px] mx-auto px-4 h-16 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <a href="/" className="text-gray-400 hover:text-gray-600 transition-colors" data-testid="admin-back-home">
          <ArrowLeft className="w-5 h-5" />
        </a>
        <div>
          <h1 className="text-lg font-bold text-gray-900">Digitize CMS</h1>
          <p className="text-xs text-gray-400">Content Management</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2 bg-[#FF6B35] text-white rounded-lg font-medium text-sm hover:bg-[#ff8555] transition-colors disabled:opacity-60"
          data-testid="admin-save-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          onClick={onLogout}
          className="flex items-center gap-2 px-4 py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors"
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </div>
  </header>
);

// ── Sidebar ──
const Sidebar = ({ activeSection, onSelect }) => (
  <nav className="w-56 flex-shrink-0">
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden sticky top-24">
      {SECTIONS.map((s) => (
        <button
          key={s.key}
          onClick={() => onSelect(s.key)}
          className={`w-full text-left px-4 py-3 text-sm font-medium border-b border-gray-100 last:border-0 transition-colors ${
            activeSection === s.key
              ? 'bg-[#FF6B35]/5 text-[#FF6B35] border-l-2 border-l-[#FF6B35]'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
          data-testid={`admin-sidebar-${s.key}`}
        >
          {s.label}
        </button>
      ))}
    </div>
  </nav>
);

// ── Loading Spinner ──
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="animate-spin w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full" />
  </div>
);

// ── Main Dashboard ──
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('company_info');
  const [sectionData, setSectionData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const redirectToLogin = useCallback(() => navigate('/admin'), [navigate]);

  const fetchAll = useCallback(async () => {
    try {
      const res = await apiClient.get('/cms/content');
      setSectionData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        redirectToLogin();
      }
    } finally {
      setLoading(false);
    }
  }, [redirectToLogin]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const saveSection = useCallback(async () => {
    setSaving(true);
    try {
      await apiClient.put(`/cms/content/${activeSection}`, { data: sectionData[activeSection] });
      setToast({ message: 'Saved successfully!', type: 'success' });
    } catch (err) {
      if (err.response?.status === 401) {
        redirectToLogin();
        return;
      }
      setToast({ message: 'Failed to save. Please try again.', type: 'error' });
    } finally {
      setSaving(false);
    }
  }, [activeSection, sectionData, redirectToLogin]);

  const updateSectionData = useCallback((data) => {
    setSectionData((prev) => ({ ...prev, [activeSection]: data }));
  }, [activeSection]);

  const handleLogout = useCallback(async () => {
    try {
      await apiClient.post('/admin/logout');
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Logout request failed:', err);
      }
    }
    redirectToLogin();
  }, [redirectToLogin]);

  const closeToast = useCallback(() => setToast(null), []);

  if (loading) return <LoadingSpinner />;

  const activeLabel = SECTIONS.find((s) => s.key === activeSection)?.label;

  return (
    <div className="min-h-screen bg-gray-50">
      {toast && <Toast {...toast} onClose={closeToast} />}
      <TopBar saving={saving} onSave={saveSection} onLogout={handleLogout} />

      <div className="max-w-[1400px] mx-auto px-4 py-6 flex gap-6">
        <Sidebar activeSection={activeSection} onSelect={setActiveSection} />
        <main className="flex-1 min-w-0">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-1">{activeLabel}</h2>
            <p className="text-gray-400 text-sm mb-6">Edit the content below and click Save Changes.</p>
            <SectionEditor
              activeSection={activeSection}
              currentData={sectionData[activeSection]}
              updateSectionData={updateSectionData}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
